import { Text } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import type { ReactElement } from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { fromEvent } from 'rxjs';
import { DoubleSide, type Group, Vector3 } from 'three';

import { TOOL_CONST } from '@/constant';
import { useSubscriptions } from '@/package';
import { useToolSettingStore } from '@/store';

import { useToolPcdCanvasContext } from './ToolPcdCanvas';
import { VGridBase } from './VGridBase';

const MAX_DISTANCE = 320 * 4;
const TARGET = new Vector3(0, 0, 0);

type GridInterval = {
  distance: number;
  gap: number;
  fontSize?: number;
};

const GRID_DEFAULT_INTERVAL: GridInterval = {
  distance: -Infinity,
  gap: 2,
  fontSize: 0.5,
};

const GRID_INTERVALS: GridInterval[] = [
  {
    distance: 200 * 200,
    gap: 40,
    fontSize: 6,
  },
  {
    distance: 80 * 80,
    gap: 20,
    fontSize: 2,
  },
  {
    distance: 20 * 20,
    gap: 10,
    fontSize: 1,
  },
  {
    distance: 10 * 10,
    gap: 2,
    fontSize: 0.5,
  },
] as const;

const zoomToDistance = (x: number) => {
  const logXMin = Math.log(10);
  const logXMax = Math.log(GRID_INTERVALS[3].distance);
  const logRange = logXMax - logXMin;

  const t = (Math.log(x) - logXMin) / logRange;

  return (
    GRID_INTERVALS[0].distance *
    Math.pow(GRID_INTERVALS[2].distance / GRID_INTERVALS[0].distance, t)
  );
};

const ToolGrid = memo((): ReactElement => {
  const { camera, controls } = useThree();
  const { viewMode, isViewMode } = useToolPcdCanvasContext();
  const { gridColor, gridShow, gridTextColor } = useToolSettingStore();
  const groupRef = useRef<Group | null>(null);
  const getDistanceSquared = () => {
    const target = controls?.target ?? TARGET;
    const targetDistanceSquared = camera.position.distanceToSquared(target);

    if (camera.isPerspectiveCamera) return targetDistanceSquared;

    const zoom = Math.max(camera.zoom, 0.001);

    return zoomToDistance(zoom);
  };

  const [distanceSquared, setDistanceSquared] = useState(getDistanceSquared);
  const { gap, fontSize = 0.25 } = isViewMode.default
    ? (GRID_INTERVALS.find(({ distance }) => distanceSquared >= distance) ??
      GRID_DEFAULT_INTERVAL)
    : GRID_DEFAULT_INTERVAL;

  const scale = useMemo(() => {
    if (
      !gridShow ||
      (viewMode !== TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP &&
        viewMode !== TOOL_CONST.TOOL_CAMERA_VIEW_MODE.DEFAULT)
    )
      return [];

    const scale: ReactElement[] = [
      <Text
        key={`0`}
        position={[0, 0, 0]}
        fontSize={fontSize}
        color={gridTextColor}
        anchorY="top"
        rotation={[Math.PI / 2, 0, 0]}
      >
        {`${0}m`}
      </Text>,
    ];

    for (let i = -MAX_DISTANCE / 2; i <= MAX_DISTANCE / 2; i += gap) {
      if (i === 0) {
        continue;
      }

      scale.push(
        <Text
          key={`x.${i}`}
          position={[i, 0, 0]}
          fontSize={fontSize}
          color={gridTextColor}
          anchorY="top"
          rotation={[Math.PI / 2, 0, 0]}
        >
          {`${i}m`}
        </Text>,
      );
      scale.push(
        <Text
          key={`y.${i}`}
          position={[0, 0, i]}
          fontSize={fontSize}
          color={gridTextColor}
          anchorY="top"
          rotation={[Math.PI / 2, 0, 0]}
        >
          {`${i}m`}
        </Text>,
      );
    }
    return scale;
  }, [fontSize, gap, gridShow, gridTextColor, viewMode]);

  useSubscriptions(() => {
    return controls
      ? [
          fromEvent(controls, 'change').subscribe(() => {
            setDistanceSquared(getDistanceSquared());
          }),
        ]
      : [];
  }, [camera, controls]);

  useEffect(() => {
    if (!groupRef.current) {
      return;
    }

    groupRef.current.updateMatrix();
  }, [gap]);

  return (
    <group
      matrixAutoUpdate={false}
      ref={groupRef}
      rotation={[-Math.PI / 2, 0, 0]}
      visible={gridShow}
    >
      <VGridBase
        args={[MAX_DISTANCE, MAX_DISTANCE]}
        rotation={[0, 0, 0]}
        cellColor={gridColor}
        cellSize={gap}
        cellThickness={1}
        controlsTarget={controls?.target}
        fadeDistance={
          isViewMode.default ? Math.sqrt(distanceSquared) : undefined
        }
        fadeFrom={2}
        fadeStrength={2}
        sectionColor={gridColor}
        sectionSize={gap}
        sectionThickness={1}
        side={DoubleSide}
      />
      {scale}
    </group>
  );
});

ToolGrid.displayName = 'ToolGrid';

export default ToolGrid;
